import {endOfDay, startOfDay} from 'date-fns';
import {combine, guard, merge, sample} from 'effector';

import {ProductOperation} from 'core/dictionaries';
import {RestError, showRestError} from 'core/errors';
import {initFiltersTable} from 'core/hooks/grid2.0';
import {InnerFilterValue, InnerFilters} from 'types';
import {ProductOperationFilter, ProductOperationsAccessors} from 'types/products-detail';

import {
    fetchDataFx,
    fetchOperationsDataFx,
    operationsData$,
    commonData$,
    Gate,
    error$,
    OriginalPhotoGate,
    fetchOriginalPhotoFx,
    fetchRemarkingDataFx,
    originalPhoto$,
    originalPhotoError$,
    remarkingData$,
    remarkingError$,
    codeTracingData$,
    filters$,
    filtersChanged,
    filtersReset,
    defaultFilters,
    stateReset,
} from './index';

const getOptionValue = (value: InnerFilterValue) => {
    if (Array.isArray(value)) return undefined;
    if (value instanceof Date) return undefined;
    if (typeof value === 'object') return value?.value;

    return value;
};

const getTextValue = (value: InnerFilterValue) => {
    const text = typeof value === 'string' ? value.trim() : undefined;

    return text || undefined;
};

const getOperationFilter = (filters: InnerFilters): ProductOperationFilter | undefined => {
    const operation = getOptionValue(filters[ProductOperationsAccessors.documentType]);
    const dateRange = filters[ProductOperationsAccessors.date];
    const documentNumber = getTextValue(filters[ProductOperationsAccessors.documentId]);
    const documentSender = getTextValue(filters[ProductOperationsAccessors.clientName]);
    const storageLocationId = Number(
        getTextValue(filters[ProductOperationsAccessors.storageLocation]),
    );
    const operationFilter: ProductOperationFilter = {};

    if (operation) operationFilter.operations = [operation as ProductOperation];

    if (Array.isArray(dateRange)) {
        const [dateFrom, dateTo] = dateRange;

        if (dateFrom instanceof Date)
            operationFilter.changeDateFrom = startOfDay(dateFrom).toISOString();
        if (dateTo instanceof Date) operationFilter.changeDateTo = endOfDay(dateTo).toISOString();
    }

    if (documentNumber) operationFilter.documentNumber = documentNumber;
    if (documentSender) operationFilter.documentSender = documentSender;
    if (Number.isFinite(storageLocationId)) operationFilter.storageLocationId = storageLocationId;

    return Object.keys(operationFilter).length ? operationFilter : undefined;
};

commonData$.on(fetchDataFx.done, (_, {result}) => result).reset(Gate.close);
operationsData$
    .on(fetchDataFx.done, (_, {result}) => result.operations)
    .on(fetchOperationsDataFx.done, (_, {result}) => result.operations)
    .reset(Gate.close);
initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
codeTracingData$
    .on(fetchDataFx.done, (_, {result}) => ({operations: result.operations, tracing: []}))
    .reset(Gate.close);
error$
    .on(fetchDataFx.fail, (_, {error}: {error: Error}) => error as RestError)
    .reset(fetchDataFx, fetchDataFx.done, Gate.open, Gate.close);

const operationFiltersChanged = sample({
    clock: merge([filtersChanged, filtersReset]),
    source: combine({
        params: Gate.state,
        filters: filters$,
    }),
    fn: ({params, filters}) => ({
        ...params,
        operationFilter: getOperationFilter(filters),
    }),
});

guard({
    source: Gate.state,
    filter: ({gtin, markingCode}) => gtin !== undefined && !!markingCode,
    target: fetchDataFx,
});

guard({
    source: operationFiltersChanged,
    filter: ({gtin, markingCode}) => gtin !== undefined && !!markingCode,
    target: fetchOperationsDataFx,
});

sample({
    clock: OriginalPhotoGate.open,
    source: OriginalPhotoGate.state,
    target: fetchOriginalPhotoFx,
});

originalPhoto$.on(fetchOriginalPhotoFx.doneData, (_, data) => data).reset(OriginalPhotoGate.close);
originalPhotoError$.on(fetchOriginalPhotoFx.fail, () => true).reset(OriginalPhotoGate.close);

fetchDataFx.fail.watch(({error}) => showRestError(error as RestError));
fetchOperationsDataFx.fail.watch(({error}) => showRestError(error as RestError));

guard({
    source: fetchDataFx.doneData,
    filter: ({replacedByMarkingCodeId, replacesMarkingCodeId}) =>
        Boolean(replacedByMarkingCodeId || replacesMarkingCodeId),
    target: fetchRemarkingDataFx,
});

remarkingData$.on(fetchRemarkingDataFx.doneData, (_, data) => data).reset(Gate.close);
remarkingError$
    .on(fetchRemarkingDataFx.fail, () => true)
    .reset(fetchRemarkingDataFx, fetchRemarkingDataFx.done, Gate.close);

fetchRemarkingDataFx.fail.watch(({error}) => showRestError(error as RestError));
